import { useEffect } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useBookEditorSession } from '../api/queries/bookSession';

const enginePath: Record<string, string> = {
  editor: '/books/$bookId',
  outline: '/books/$bookId/outline',
  'story-studio': '/books/$bookId/story-studio',
  consistency: '/books/$bookId/consistency',
  board: '/books/$bookId/boards',
  // "world-builder" is standalone (/worlds/$worldId) — fall through to the editor when
  // that's the stored engine since there's no book-scoped path for it.
};

/**
 * Landing page for a book — looks up the creator's BookEditorSession and redirects to
 * the last engine they used (Phase 3 W17 cross-engine resume). First-time visits fall
 * through to the editor.
 */
export function BookResumeRoute() {
  const { bookId } = useParams({ strict: false }) as { bookId: string };
  const session = useBookEditorSession(bookId);
  const navigate = useNavigate();

  useEffect(() => {
    if (session.isLoading) return;
    const target = session.data?.lastEngine
      ? enginePath[session.data.lastEngine]
      : null;
    if (target && target !== '/books/$bookId') {
      void navigate({ to: target, params: { bookId }, replace: true });
    } else {
      void navigate({
        to: '/books/$bookId',
        params: { bookId },
        replace: true,
      });
    }
  }, [session.isLoading, session.data?.lastEngine, bookId, navigate]);

  return (
    <div style={{ padding: 24 }}>
      <CircularProgress size={20} />
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Resuming…
      </Typography>
    </div>
  );
}
